import React, { FunctionComponent, useContext, useEffect, useRef } from 'react'
import { useTranslation } from '../../utils/convergence'
import {
  useApplePayState,
  useCheckoutState,
  useHasUserInitiatedChangedPaymentMethod,
  useIsAfterpayFinanceFeeEligible,
  usePreferredBankAccount,
  usePreferredCard,
  usePreferredPaymentMethod,
  useSelectedPaymentMethod,
  useSelectedPaymentType,
} from '../../state'
import { isCardScanRequiredErrorCode, isSavedCard, PaymentMethodType, RecoverableErrorCode, ApplePayError, hasCardTopazErrorReason } from '../../model'
import { ErrorCode } from '@afterpaytouch/portal-api'
import { isNullOrUndefined, valueIsNotNullOrUndefined } from '@afterpay/utils'
import { ErrorText } from '../'
import { Trans } from '../../utils/convergence'
import { isKeyWithInlineContext, selectCardErrorMessage } from './utils'
import { SwitchContext } from './types'
import styles from './style.module.css'
import { Link, Text, DesignLanguage, useIsDesignLanguage } from '@afterpaytouch/core'
import { useCheckoutSizes } from '../../utils/responsive'
import { useAddPaymentLinkHandler } from './hooks'
import { SummaryNotificationContext } from './SummaryNotificationContext'
import { buildTranslationKey } from '../../utils/locales'
import { PaymentType } from '@afterpay/types'

type SummaryNotificationSource = 'preferredBankAccount' | 'preferredCard' | 'checkoutError' | 'applePay'

export const SummaryNotification: FunctionComponent = () => {
  const { setNotification, setClicked, notification } = useContext(SummaryNotificationContext)
  const notificationSource = useRef<SummaryNotificationSource>()
  const { t } = useTranslation()
  const { card: preferredCard, valid: isPreferredCardValid, reason, switched } = usePreferredCard() ?? {}
  const { account, valid: bankAccountValid, reason: bankAccountReason, switched: bankAccountSwitched } = usePreferredBankAccount() ?? {}
  const preferredPaymentMethod = usePreferredPaymentMethod()
  const selectedPaymentMethod = useSelectedPaymentMethod()
  const hasUserInitiatedChangedPaymentMethod = useHasUserInitiatedChangedPaymentMethod()
  const { error } = useCheckoutState()
  const applepay = useApplePayState()
  const showNotificationForNonSwitchedTopazError =
    !hasUserInitiatedChangedPaymentMethod && preferredPaymentMethod === PaymentMethodType.CREDIT_CARD
      ? !switched && hasCardTopazErrorReason(preferredCard)
      : false

  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const { smallTextSize } = useCheckoutSizes()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const selectedPaymentType = useSelectedPaymentType()
  const isAFFPBI = isAFF && selectedPaymentType !== PaymentType.PCL
  const addPaymentMethodHandler = useAddPaymentLinkHandler()

  const getMessageWithLink = (messageKey: string, context?: string, values?: Record<string, unknown>): React.ReactNode => {
    return (
      <Text size={smallTextSize} testNameSpace='summary-notification'>
        <Trans
          i18nKey={messageKey}
          context={context}
          values={values}
          components={{
            addPaymentMethodLink: <Link kind='Underline' testNameSpace='summary-notification' onClick={addPaymentMethodHandler} target='_blank' />,
          }}
        />
      </Text>
    )
  }

  const selectedSavedCardToken =
    selectedPaymentMethod !== undefined &&
    selectedPaymentMethod.type === PaymentMethodType.CREDIT_CARD &&
    selectedPaymentMethod.card !== undefined &&
    isSavedCard(selectedPaymentMethod.card)
      ? selectedPaymentMethod.card.token
      : undefined
  const hasSelectedNonCardPaymentMethod = selectedPaymentMethod !== undefined && selectedPaymentMethod.type !== PaymentMethodType.CREDIT_CARD
  const hasSelectedDifferentSavedCard = selectedSavedCardToken !== undefined && selectedSavedCardToken !== preferredCard?.token
  const hasSelectedAlternativePaymentMethod = hasSelectedNonCardPaymentMethod || hasSelectedDifferentSavedCard

  // clear notification conditions
  useEffect(() => {
    const shouldClearErrorOnSelectingPaymentMethod =
      hasUserInitiatedChangedPaymentMethod &&
      !isNullOrUndefined(reason) &&
      preferredPaymentMethod === PaymentMethodType.CREDIT_CARD &&
      hasSelectedAlternativePaymentMethod &&
      notificationSource.current === 'preferredCard'
    const hasActiveNotification = valueIsNotNullOrUndefined(notification)
    const hasNoCheckoutError = isNullOrUndefined(error) && isNullOrUndefined(applepay.error)
    const hasNoPreferredCardError = isNullOrUndefined(reason)
    const shouldClearNotification = hasActiveNotification && hasNoCheckoutError && (hasNoPreferredCardError || shouldClearErrorOnSelectingPaymentMethod)

    if (shouldClearNotification) {
      notificationSource.current = undefined
      setNotification(undefined)
    }
  }, [
    notification,
    error,
    applepay.error,
    setNotification,
    reason,
    preferredPaymentMethod,
    hasUserInitiatedChangedPaymentMethod,
    hasSelectedAlternativePaymentMethod,
  ])

  // errors on preferred payment method
  useEffect(() => {
    const shouldShowCardErrorAfterPaymentMethodChange = !isPreferredCardValid && !hasSelectedAlternativePaymentMethod
    const shouldShowCardErrorNotification =
      !isNullOrUndefined(preferredCard) && !isNullOrUndefined(reason) && (!hasUserInitiatedChangedPaymentMethod || shouldShowCardErrorAfterPaymentMethodChange)

    if (preferredPaymentMethod === PaymentMethodType.ACH_BANK_ACCOUNT && !isNullOrUndefined(account) && !isNullOrUndefined(bankAccountReason)) {
      const switchContext = bankAccountSwitched ? SwitchContext.Switched : SwitchContext.CannotSwitch

      // The "switched" message only exists for inline arcade components.
      const context = isArcadeDesignLanguage && bankAccountSwitched && isKeyWithInlineContext(bankAccountReason, switchContext) ? 'switchedInline' : ''

      const messageKey = buildTranslationKey({
        namespace: 'summary',
        segments: [
          'bankAccountErrors',
          bankAccountReason,
          {
            contexts: [[isAFFPBI && bankAccountReason === 'noValidCard', `message_aff`]],
            default: 'message',
          },
        ],
      })
      notificationSource.current = 'preferredBankAccount'
      setNotification({
        // eslint-disable-next-line @afterpay/i18n-only-static-keys
        heading: t(`summary:bankAccountErrors.${bankAccountReason}.heading`),
        message: getMessageWithLink(messageKey, context),
        kind: 'Error',
        severe: !bankAccountValid,
      })
    } else if (shouldShowCardErrorNotification) {
      const baseContext = switched ? SwitchContext.Switched : SwitchContext.CannotSwitch

      const { messageKey, context } = selectCardErrorMessage({
        reason,
        isArcadeDesignLanguage,
        switched,
        cardExpiryStatus: preferredCard?.cardExpiryStatus,
        isAFFPBI,
        preferredPaymentMethod,
      })

      // The cardExpiryLimitInMonths message/heading interpolate "{{count}}" (i18next count-based
      // plural); supply it from the card (set by setCardsForExpiryLimit) so the redesign's inline
      // notification doesn't leak a literal "{{count}}" (build 3973). Only attach it when a limit
      // applies — other reasons render exactly as before.
      const messageValues = preferredCard?.cardExpiryLimitInMonths !== undefined ? { count: preferredCard.cardExpiryLimitInMonths } : undefined

      // eslint-disable-next-line @afterpay/i18n-only-static-keys, @afterpay/i18n-no-dynamic-context
      const message = isArcadeDesignLanguage ? getMessageWithLink(messageKey, context, messageValues) : t(messageKey, { context, ...messageValues })

      const inlineKind = baseContext === SwitchContext.CannotSwitch ? 'Error' : 'Info'

      notificationSource.current = 'preferredCard'
      setNotification({
        // eslint-disable-next-line @afterpay/i18n-only-static-keys, @afterpay/i18n-no-dynamic-context
        heading: t(`summary:error.${reason}.heading`, { context: baseContext, ...messageValues }),
        message,
        kind: isArcadeDesignLanguage ? inlineKind : 'Error',
        severe: !isPreferredCardValid,
      })
    }
  }, [
    preferredCard,
    isPreferredCardValid,
    reason,
    switched,
    account,
    bankAccountValid,
    bankAccountSwitched,
    preferredPaymentMethod,
    bankAccountReason,
    hasUserInitiatedChangedPaymentMethod,
    isArcadeDesignLanguage,
    hasSelectedAlternativePaymentMethod,
    setNotification,
  ])

  // errors set by checkout & on confirm
  useEffect(() => {
    if (error?.error !== 'undefined' && isCardScanRequiredErrorCode(error?.error as ErrorCode)) {
      return setClicked(false)
    } else if (error?.error !== undefined && (error?.error as ErrorCode) === ErrorCode.CardNotEnabled) {
      // confirm can trigger card_not_enabled if the card is not a supported brand
      setClicked(false)
    } else if (error?.error !== undefined && Object.values(RecoverableErrorCode).includes(error?.error as RecoverableErrorCode)) {
      setClicked(false)
      const messageKey = buildTranslationKey({
        namespace: 'summary',
        segments: [
          'error',
          error?.error,
          {
            contexts: [[isAFFPBI, `message_aff`]],
            default: 'message',
          },
        ],
      })
      const inlineContext = isKeyWithInlineContext(error.error) ? 'inline' : undefined

      // eslint-disable-next-line @afterpay/i18n-only-static-keys
      const message = isArcadeDesignLanguage ? getMessageWithLink(messageKey, inlineContext) : t(messageKey)

      notificationSource.current = 'checkoutError'
      setNotification({
        // eslint-disable-next-line @afterpay/i18n-only-static-keys
        heading: t(`summary:error.${error?.error}.heading`),
        message,
        kind: 'Error',
        severe: false,
      })
    } else if (applepay.error !== undefined) {
      setClicked(false)
      if (applepay?.error === ApplePayError.InvalidCardType) {
        notificationSource.current = 'applePay'
        setNotification({
          heading: t('summary:error.prepaidCard.heading'),
          message: t('summary:error.prepaidCard.message'),
          kind: 'Error',
          severe: true,
        })
      } else if (applepay?.error === ApplePayError.BinBlocked) {
        setNotification({
          heading: t('summary:error.unsupportedCardIssuerBank.heading'),
          message: t('summary:error.unsupportedCardIssuerBank.message'),
          kind: 'Error',
          severe: true,
        })
      } else {
        notificationSource.current = 'applePay'
        setNotification({
          heading: t('summary:error.applePay.heading'),
          message: t('summary:error.applePay.message'),
          kind: 'Error',
          severe: true,
        })
      }
    }
  }, [error, applepay.error, isArcadeDesignLanguage, setClicked, setNotification])

  const containerClass = isArcadeDesignLanguage ? styles.inlineContainer : styles.container

  // (Non-inline) Some topaz errors are shown elsewhere in the UI, so we don't want to show them here
  const nonInlineShouldShowNotification = notification !== undefined && !showNotificationForNonSwitchedTopazError

  // (Inline) All errors are shown in the summary notification
  const inlineShouldShowNotification = notification !== undefined

  const shouldShowNotification = isArcadeDesignLanguage ? inlineShouldShowNotification : nonInlineShouldShowNotification

  return shouldShowNotification ? (
    <div className={containerClass}>
      <ErrorText testNamespace='summary-notification' message={notification?.message} kind={notification?.kind} />
    </div>
  ) : null
}
